import { useEffect } from 'react';
import { Activity, Users, Upload, ListChecks, Globe, ShieldCheck } from 'lucide-react';
import { useAdminOverviewStore } from '../../store/adminOverviewStore';
import { StatusCard } from '../../components/admin/StatusCard';

export function OverviewTab() {
  const { summary, loading, error, fetch, startPolling, stopPolling } = useAdminOverviewStore();

  useEffect(() => {
    fetch();
    startPolling();
    return () => { stopPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="font-medium">Couldn't load overview</div>
        <div>{error}</div>
        <button className="mt-2 text-xs underline" onClick={() => fetch()}>Retry</button>
      </div>
    );
  }

  if (!summary && !loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">No data yet.</div>;
  }
  if (!summary) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>;
  }

  const blast = summary.syncs.lastRunBySyncName['datamart:blast'];
  const nightlySyncStatus = blast?.status ?? 'UNKNOWN';
  const syncTone =
    summary.syncs.anyInFlight ? 'warn'
    : nightlySyncStatus === 'SUCCESS' ? 'ok'
    : nightlySyncStatus === 'FAILED' ? 'error'
    : 'idle';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
      <StatusCard
        to="/admin/syncs"
        icon={Activity}
        label="Last nightly sync"
        primary={nightlySyncStatus}
        secondary={blast?.startedAt ? new Date(blast.startedAt).toLocaleString() : '—'}
        tone={syncTone}
      />
      <StatusCard
        to="/admin/matching"
        icon={Users}
        label="Pending matches"
        primary={String(summary.matching.pendingReview)}
        tone={summary.matching.pendingReview > 0 ? 'warn' : 'idle'}
      />
      <StatusCard
        to="/admin/imports"
        icon={Upload}
        label="Active imports"
        primary={String(summary.imports.active)}
        secondary={summary.imports.failedLast24h > 0 ? `${summary.imports.failedLast24h} failed (24h)` : undefined}
        tone={summary.imports.failedLast24h > 0 ? 'error' : 'idle'}
      />
      <StatusCard
        to="/admin/audit/timeline"
        icon={ListChecks}
        label="Audit changes (24h)"
        primary={String(summary.audit.changesLast24h)}
        tone="idle"
      />
      <StatusCard
        to="/admin/competitor-scraping"
        icon={Globe}
        label="Competitor scrape"
        primary={summary.competitorScraping.errorsLast7d === 0 ? 'Healthy' : `${summary.competitorScraping.errorsLast7d} err`}
        tone={summary.competitorScraping.errorsLast7d > 0 ? 'error' : 'ok'}
      />
      <StatusCard
        to="/admin/backups"
        icon={ShieldCheck}
        label="Backups (Prod)"
        primary={summary.backups.primary}
        secondary={summary.backups.secondary}
        tone={summary.backups.tone}
      />
    </div>
  );
}
